import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';
import { Dropdown } from 'primeng/dropdown';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { ValidatorService } from './validator.service';

@Directive({
  selector: '[appValidator]',
  exportAs: 'appValidator',
})
export class ValidatorDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;

  constructor(private validatorService: ValidatorService, private changeDetectorRef: ChangeDetectorRef, private elementRef: ElementRef) {
    /** */
  }

  ngOnDestroy(): void {
    this.validatorService.clear();
  }

  ngOnInit() {
    /** */
  }

  ngAfterViewInit() {
    this.bindEvents();
  }

  /**
   *  method to bind blur event for every input in specific formGroup.
   *  isInvalidForm is a default param.
   *  If you want to hold disable event over any button only passing the value with this function.
   */
  bindEvents(): void {
    this.changeDetectorRef.detectChanges();
    this.formGroup.statusChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.formGroup.get('isInvalidForm').setValue(this.formGroup.invalid);
    });
    for (const id of Object.keys(this.formGroup.controls)) {
      const element = this.elementRef.nativeElement.querySelector('#' + id);
      if (element) {
        const control = this.formGroup.get(id);
        this.bindControl(control, id);
        if (element.tagName === 'P-DROPDOWN') {
          this.bindPrimeControl(control, id, this.validatorService.getDropDown(id));
        } else {
          this.bindBlur(element, id);
        }
      }
    }
  }

  /**
   *  method to validate all controls in specific formGroup.
   */
  @HostListener('submit')
  validateControls() {
    this.changeDetectorRef.detectChanges();
    for (const id of Object.keys(this.formGroup.controls)) {
      if (id !== 'isInvalidForm') {
        this.validateControl(id);
      }
    }
  }

  /**
   *  method to check if a formControl has any error.
   *  @param {string} id ID of formControl
   *  @return {} none, empty object or null if the formControl does not have any error
   */
  validateControl(id: string): void {
    if(this.formGroup.touched){
      this.validatorService.validateControl(this.formGroup, id, this.changeDetectorRef);
    }
  }

  /**
   * bind control
   * @param {AbstractControl} control
   * @param {string} id
   */
  bindControl(control: AbstractControl, id: string): void {
    const self = this;
    control.valueChanges.pipe(debounceTime(250)).subscribe(() => {
      if (!control.dirty) {
        return environment.guardClause;
      }
      setTimeout(() => self.validateControl(id));
    });
  }

  /**
   * bind dropdown
   * @param {AbstractControl} control
   * @param {string} id
   * @param {Dropdown | AutoComplete} element
   */
  bindPrimeControl(control: AbstractControl, id: string, element: Dropdown | AutoComplete, bindBlur?: boolean): void {
    if (element) {
      element.onHide.subscribe(() => {
        this.primeControlProcess(control, id);
      });
    }
    if (element && bindBlur) {
      element.onBlur.subscribe(() => {
        this.primeControlProcess(control, id);
      });
    }
  }

  /**
   * process prime control
   * @param {AbstractControl} control
   * @param {string} id
   */
  primeControlProcess(control: AbstractControl, id: string): void {
    if (!control.dirty) {
      this.validateControl(id);
    }
    return environment.guardClause;
  }

  /**
   * bind blur action
   * @param {HTMLElement} element
   * @param {string} id
   */
  bindBlur(element: HTMLElement, id: string): void {
    const self = this;
    element.addEventListener('blur', () => {
      self.validateControl(id);
    });
  }
}
